import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ArrowDownCircle, ArrowUpCircle, RefreshCw, TrendingUp, Users } from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

const PERIODS = ["today", "week", "month", "custom"];
const PERIOD_LABELS = { today: "Today", week: "This Week", month: "This Month", custom: "Custom" };

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function rangeForPeriod(period, customFrom, customTo) {
  const now = new Date();
  const today = toISODate(now);
  if (period === "today") return { from: today, to: today };
  if (period === "week") {
    const day = now.getDay(); // 0 = Sunday
    const diffToMonday = day === 0 ? 6 : day - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - diffToMonday);
    return { from: toISODate(monday), to: today };
  }
  if (period === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toISODate(first), to: today };
  }
  return { from: customFrom || today, to: customTo || today };
}

function AdminDashboard() {
  const [stats, setStats] = useState({ totalUsers: 0, totalWalletBalance: 0, todayTotalBids: 0, activePlayers: 0 });
  const [rangeStats, setRangeStats] = useState({ totalDeposit: 0, totalWithdraw: 0, depositCount: 0, withdrawCount: 0 });
  const [loading, setLoading] = useState(false);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [period, setPeriod] = useState("today");
  const [customFrom, setCustomFrom] = useState(() => toISODate(new Date()));
  const [customTo, setCustomTo] = useState(() => toISODate(new Date()));

  const { from, to } = useMemo(() => rangeForPeriod(period, customFrom, customTo), [period, customFrom, customTo]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/admin/stats");
      setStats((s) => ({ ...s, ...(res.data || {}) }));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRangeStats = useCallback(async () => {
    setRangeLoading(true);
    try {
      const res = await API.get("/stats-range", { params: { from, to } });
      setRangeStats((s) => ({ ...s, ...(res.data || {}) }));
    } catch (err) {
      console.log(err);
    } finally {
      setRangeLoading(false);
    }
  }, [from, to]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRangeStats(); }, [loadRangeStats]);

  const fmt = (v) => {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toLocaleString("en-IN")}`;
  };

  const profit = Number(rangeStats.totalDeposit || 0) - Number(rangeStats.totalWithdraw || 0);
  const refreshing = loading || rangeLoading;

  const refreshAll = () => { loadStats(); loadRangeStats(); };

  return (
    <AdminLayout>
      <div style={wrap}>
        <div style={pageHeader}>
          <div>
            <h1 style={title}>Dashboard</h1>
            <p style={subtitle}>Platform overview & analytics</p>
          </div>
          <button style={refreshBtn} onClick={refreshAll} disabled={refreshing} title="Refresh">
            <RefreshCw size={16} color="#f97316" style={refreshing ? { animation: "spin 1s linear infinite" } : {}} />
          </button>
        </div>

        {/* ── period filter ── */}
        <div style={periodBar}>
          <div style={periodChips}>
            {PERIODS.map((p) => (
              <button key={p} style={periodChip(period === p)} onClick={() => setPeriod(p)}>
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div style={customRow}>
              <input type="date" style={dateInp} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <span style={{ color: "#94a3b8", fontSize: 13 }}>→</span>
              <input type="date" style={dateInp} value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
          )}
          <span style={rangeLabel}>{from === to ? from : `${from} → ${to}`}</span>
        </div>

        <div style={grid}>
          <StatCard
            icon={<ArrowUpCircle size={26} />}
            label={`Deposits (${PERIOD_LABELS[period]})`}
            value={fmt(rangeStats.totalDeposit)}
            sub={`${rangeStats.depositCount || 0} transactions`}
            from="#22c55e" to="#16a34a"
            emoji="💰"
          />
          <StatCard
            icon={<ArrowDownCircle size={26} />}
            label={`Withdrawals (${PERIOD_LABELS[period]})`}
            value={fmt(rangeStats.totalWithdraw)}
            sub={`${rangeStats.withdrawCount || 0} transactions`}
            from="#ef4444" to="#dc2626"
            emoji="📤"
          />
          <StatCard
            icon={<TrendingUp size={26} />}
            label={`Net Profit (${PERIOD_LABELS[period]})`}
            value={fmt(profit)}
            from="#f97316" to="#ea580c"
            emoji="📈"
          />
          <StatCard
            icon={<Users size={26} />}
            label="Total Users"
            value={Number(stats.totalUsers || 0).toLocaleString("en-IN")}
            from="#f97316" to="#ea580c"
            emoji="👥"
          />
        </div>

        <div style={quickGrid}>
          <QuickLink label="Deposits" emoji="💵" sub="Pending approvals" path="/admin/deposits" color="#22c55e" />
          <QuickLink label="Withdrawals" emoji="📤" sub="Pending payouts" path="/admin/payouts" color="#ef4444" />
          <QuickLink label="Users" emoji="👤" sub="Manage players" path="/admin/users" color="#f97316" />
          <QuickLink label="Results" emoji="🏆" sub="Declare results" path="/admin/results" color="#f59e0b" />
          <QuickLink label="Starline" emoji="⭐" sub="Starline control" path="/admin/starline" color="#f97316" />
          <QuickLink label="Settings" emoji="⚙️" sub="App configuration" path="/admin/settings" color="#0ea5e9" />
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, sub, from, to, emoji }) {
  return (
    <div style={{ ...card, background: `linear-gradient(135deg, ${from}, ${to})` }}>
      <div style={cardEmoji}>{emoji}</div>
      <div style={cardBody}>
        <p style={cardLabel}>{label}</p>
        <p style={cardValue}>{value}</p>
        {sub && <p style={cardSub}>{sub}</p>}
      </div>
      <div style={cardIconWrap}>{React.cloneElement(icon, { color: "rgba(255,255,255,0.3)" })}</div>
    </div>
  );
}

function QuickLink({ label, emoji, sub, path, color }) {
  return (
    <a href={path} style={{ ...quickCard, borderTop: `3px solid ${color}` }}>
      <span style={quickEmoji}>{emoji}</span>
      <div>
        <div style={quickLabel}>{label}</div>
        <div style={quickSub}>{sub}</div>
      </div>
    </a>
  );
}

const wrap = { padding: "28px 24px", maxWidth: 1100, margin: "0 auto" };
const pageHeader = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 };
const title = { margin: 0, fontSize: 24, fontWeight: 900, color: "#431407" };
const subtitle = { margin: "4px 0 0", fontSize: 13, color: "#6b7280" };
const refreshBtn = { width: 40, height: 40, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, display: "grid", placeItems: "center", cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" };

const periodBar = { display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 20 };
const periodChips = { display: "flex", gap: 8, background: "#fff", padding: 4, borderRadius: 12, border: "1px solid #f1f5f9" };
const customRow = { display: "flex", alignItems: "center", gap: 8 };
const dateInp = { height: 34, border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "0 12px", fontSize: 12, color: "#334155", outline: 0 };
const rangeLabel = { fontSize: 12, color: "#94a3b8", fontWeight: 700 };

function periodChip(active) {
  return {
    height: 34, padding: "0 16px", borderRadius: 9, border: "none",
    background: active ? "#ea580c" : "transparent",
    color: active ? "#fff" : "#64748b",
    fontWeight: 700, fontSize: 12, cursor: "pointer",
  };
}

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 };
const card = { borderRadius: 20, padding: "22px 20px", display: "flex", alignItems: "center", gap: 14, color: "#fff", position: "relative", overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" };
const cardEmoji = { fontSize: 32, flexShrink: 0 };
const cardBody = { flex: 1 };
const cardLabel = { margin: 0, fontSize: 12, fontWeight: 600, opacity: 0.85 };
const cardValue = { margin: "6px 0 0", fontSize: 26, fontWeight: 900, letterSpacing: "-0.5px" };
const cardSub = { margin: "4px 0 0", fontSize: 11, fontWeight: 700, opacity: 0.75 };
const cardIconWrap = { position: "absolute", right: 16, bottom: 12 };

const quickGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 };
const quickCard = { background: "#fff", borderRadius: 16, padding: "18px 16px", display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "#1e293b", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9" };
const quickEmoji = { fontSize: 28 };
const quickLabel = { fontSize: 14, fontWeight: 800, color: "#1e293b" };
const quickSub = { fontSize: 11, color: "#94a3b8", marginTop: 2 };

export default AdminDashboard;
